import React, { useEffect, useState } from "react";
import { Mail, Phone, LifeBuoy, Send, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const ISSUE_TYPES = [
  ["payment", "Payment / refund"], ["booking", "Booking / agreement"], ["listing", "Listing problem"],
  ["verification", "Owner verification"], ["dispute", "Dispute with owner / tenant"], ["account", "Account / login"], ["other", "Something else"],
];

const FAQS = [
  ["How does booking work on LocaNest?", "Find a listing, tap “Send interest” or chat with the owner. During our free-launch phase there is no advance/token payment — you finalise the deal directly with the owner and complete it through LocaNest so both sides stay protected. When payments are enabled, you pay a small advance via Cashfree and LocaNest transfers it to the owner within 3–4 days."],
  ["How does owner verification work?", "Owners upload Aadhaar + PAN (and business proof for commercial listings) from Profile → Owner verification. Our team reviews within 24 hours and approved owners get the Verified badge on every listing."],
  ["How do refunds work?", "If an owner rejects a paid booking, the full advance is refunded to the original payment method automatically. Tenant-initiated cancellations after payment are non-refundable as per our Refund Policy. Refunds usually reflect in 5–7 working days."],
  ["I paid a broker directly and lost money — can you help?", "Payments made outside LocaNest are not covered by our protection. Always pay through LocaNest so we can hold and transfer funds safely and mediate any dispute."],
  ["How do owners receive payouts?", "Add your bank account or UPI ID in Profile → Bank details. When a tenant pays through LocaNest, we transfer the amount to you within 3–4 days and email you the reference."],
  ["Someone is asking me to pay outside the app. What should I do?", "Don’t. Report it here under “Dispute” with the listing link — our system also auto-flags such chats for admin review."],
];

export default function SupportPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", issue_type: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [mine, setMine] = useState([]);
  const [support, setSupport] = useState({ email: "official@locanest.in", phone: null, hours: "" });

  useEffect(() => { api.get("/config").then(({ data }) => data.support && setSupport(data.support)).catch(() => {}); }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
      api.get("/support/tickets/mine").then(({ data }) => setMine(data)).catch(() => {});
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.issue_type) { toast.error("Please choose an issue type"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", { ...form, phone: form.phone || null });
      setDone(data);
      toast.success(`Ticket #${data.short_id} created — we've emailed you a confirmation`);
      setForm((f) => ({ ...f, issue_type: "", message: "" }));
      if (user) api.get("/support/tickets/mine").then(({ data }) => setMine(data)).catch(() => {});
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="support-page">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Help &amp; Support</div>
      <h1 className="font-display text-4xl md:text-5xl mt-2">We&rsquo;re here to sort it out</h1>
      <p className="text-[#5C6B7A] mt-3 max-w-2xl">Payment stuck, listing issue, verification delay or a dispute — tell us what happened and a real person from LocaNest will get back within 24 hours.</p>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 rounded-xl bg-[#F0EBE1] border border-[#1A1F2C]/10 p-6 space-y-4" data-testid="support-form">
          {done && (
            <div className="rounded-md bg-[#D4DEC6]/50 border border-[#2B3A67]/20 p-3 text-sm flex gap-2" data-testid="support-success">
              <CheckCircle2 size={16} className="text-[#2B3A67] shrink-0 mt-0.5" />
              <div>Ticket <strong>#{done.short_id}</strong> logged. We&rsquo;ve emailed you a confirmation and our team has been notified.</div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Your name</Label><Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="support-name" /></div>
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="support-email" /></div>
            <div><Label>Phone (optional)</Label><Input inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile" data-testid="support-phone" /></div>
            <div>
              <Label>Issue type</Label>
              <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                <SelectTrigger data-testid="support-issue-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{ISSUE_TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`support-issue-${k}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>What happened?</Label>
            <Textarea required minLength={10} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Include listing link, booking / agreement number, and dates if relevant." data-testid="support-message" />
          </div>
          <Button type="submit" disabled={busy} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11 px-6" data-testid="support-submit">
            <Send size={14} className="mr-2" /> {busy ? "Sending…" : "Send to LocaNest support"}
          </Button>
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <div className="rounded-xl bg-[#2B3A67] text-[#FDFBF7] p-6" data-testid="support-contact-card">
            <div className="flex items-center gap-2 text-[#D4DEC6] text-xs uppercase tracking-[0.2em]"><LifeBuoy size={14} /> Direct contact</div>
            <a href={`mailto:${support.email}`} className="mt-4 flex items-center gap-3 hover:text-[#D4DEC6]" data-testid="support-email-link"><Mail size={18} /> <span>{support.email}</span></a>
            {support.phone && (
              <a href={`https://wa.me/${String(support.phone).replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=Hi%20LocaNest%20support%2C%20I%20need%20help%20with`} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-3 hover:text-[#D4DEC6]" data-testid="support-whatsapp-link"><Phone size={18} /> <span>WhatsApp / Call · {support.phone}</span></a>
            )}
            <div className="mt-4 text-xs text-[#FDFBF7]/75 flex items-center gap-1.5"><Clock size={12} /> {support.hours} · reply within 24 hours</div>
          </div>
          {user && mine.length > 0 && (
            <div className="rounded-xl bg-[#F0EBE1] border border-[#1A1F2C]/10 p-5" data-testid="support-my-tickets">
              <div className="text-xs uppercase tracking-[0.2em] text-[#5C6B7A] mb-3">Your requests</div>
              <div className="space-y-2">
                {mine.slice(0, 6).map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`my-ticket-${t.id}`}>
                    <div className="min-w-0"><span className="font-mono text-xs text-[#5C6B7A]">#{t.id.slice(0, 8)}</span> <span className="capitalize">{t.issue_type}</span><div className="text-xs text-[#5C6B7A] truncate">{t.message}</div></div>
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.16em] shrink-0 ${t.status === "resolved" ? "bg-[#D4DEC6] text-[#2B3A67]" : "bg-[#C85A32]/15 text-[#C85A32]"}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      <section className="mt-14" data-testid="support-faq">
        <h2 className="font-display text-2xl md:text-3xl">Common questions</h2>
        <Accordion type="single" collapsible className="mt-4">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger className="text-left" data-testid={`faq-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-[#5C6B7A] leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
